<template>
  <div class="grid-headline">
    <div class="select-columns-container">
      <b-dropdown aria-role="menu" trap-focus :mobile-modal="false" @active-change="onColumnsDropdownActiveChange">
        <template #trigger>
          <button class="is-transparent" aria-haspopup="true" aria-controls="dropdown-menu" :class="{ active: showSelectColumns }" @click="showSelectColumns = !showSelectColumns">
            <span class="button-text-w">
              <span class="text">{{ $t("Grid.ColumnsSelect") }}</span>
            </span>
            <icon-base width="12" height="7" view-box="0 0 12 7" icon-name="caret-down"><icon-caret-down /></icon-base>
          </button>
        </template>

        <b-dropdown-item aria-role="menu-item" :focusable="false" custom paddingless>
          <div v-for="(item, index) in columns" :key="index">
            <b-field :class-name="index" v-if="!item.forbidden">
              <b-checkbox v-model="columns[index].visible" :native-value="item.id" @change.native="onColumnSelect(columns[index])">
                {{ item.label }}
              </b-checkbox>
            </b-field>
          </div>
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="border"></div>

    <mq-layout class="search-container" mq="lg">
      <b-field>
        <icon-base width="15" height="15" view-box="0 0 15 15" icon-name="search"><icon-search /></icon-base>
        <b-input :value="searchValue" :placeholder="$t('Campaigns.Grid.Search')" @blur="onSearchChange($event)" @keyup.native.enter="onSearchChange($event)"></b-input>
      </b-field>
    </mq-layout>

    <div class="export-button-container">
      <b-button class="is-icon export-button" @click="onExportCampaignsToExcelClick()">
        <icon-base width="20" height="22" view-box="0 0 20 21.85" icon-name="excel"><icon-excel /></icon-base>
        <span>{{ $t("Grid.ExportToExcel") }}</span>
      </b-button>
    </div>
  </div>
</template>

<script lang="ts">
import VueMq from "vue-mq";
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import i18n from "@/i18n";
import { DataSourceResult } from "@/models/DataSourceResult";
import { GridColumn } from "@/models/GridColumn";
import { Result } from "@/models/Result";
import { TableSettings, TableSettingsState, TableSettingsStateColumn } from "@/models/TableSettings";
import ConfigService from "@/services/ConfigService";
import HelperService from "@/services/HelperService";
import ApiService from "@/services/api/ApiService";
import CampaignsApiService, { CampaignsPayload } from "@/services/api/CampaignsApiService";
import IconBase from "../IconBase.vue";
import IconCaretDown from "../icons/IconCaretDown.vue";
import IconExcel from "../icons/IconExcel.vue";
import IconSearch from "../icons/IconSearch.vue";
import GridTableSettingsEn from "../../data/grid-defaultsettings-en.json";
import GridTableSettingsHe from "../../data/grid-defaultsettings-he.json";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});

const namespace = "campaignsModule";

@Component({
  components: {
    IconBase,
    IconSearch,
    IconCaretDown,
    IconExcel,
  },
})
export default class CampaignsGridHeadline extends Vue {

  //#region Vuex getters and actions

  @Getter("campaigns", { namespace }) campaigns!: DataSourceResult;
  @Getter("campaignsPayload", { namespace }) campaignsPayload!: CampaignsPayload;
  @Getter("columns", { namespace }) columns!: GridColumn[];
  @Getter("searchValue", { namespace }) searchValue!: string;
  @Getter("selectedCampaignGridColumnsIds", { namespace }) selectedCampaignGridColumnsIds!: number[];
  @Getter("tableSettings", { namespace }) tableSettings!: TableSettings[];
  @Action("getCampaigns", { namespace }) getCampaigns!: (payload: CampaignsPayload) => Promise<DataSourceResult>;
  @Action("setSearchValue", { namespace }) setSearchValue!: (searchValue: string) => void;
  @Action("setSelectedCampaignGridColumnsIds", { namespace }) setSelectedCampaignGridColumnsIds!: (selectedCampaignGridColumnsIds: number[]) => void;
  @Action("setTableSettings", { namespace }) setTableSettings!: (tableSettings: TableSettings[] | undefined) => void;

  //#endregion Vuex getters and actions


  //#region Properties

  showSelectColumns = false;

  //#endregion Properties


  //#region Getters

  get campaignsTableSettings(): TableSettings {
    let settings: TableSettings | undefined = this.tableSettings.find((i: TableSettings) => i.GridId === "channelsgrid");
    if (!settings) {
      let settingsState: TableSettingsState = GridTableSettingsHe;
      switch (i18n.locale) {
        case "en-US":
          settingsState = GridTableSettingsEn;
          break;
        case "he-IL":
          settingsState = GridTableSettingsHe;
          break;
      }
      settings = {
        Culture: i18n.locale,
        GridId: "channelsgrid",
        State: JSON.stringify(settingsState),
        StateObject: settingsState,
        Url: location.pathname
      };
      this.tableSettings.push(settings);
      this.setTableSettings(this.tableSettings);
    }
    return settings;
  }

  //#endregion Getters


  //#region Handlers

  onColumnsDropdownActiveChange(show: boolean) {
    if (show === false) {
      ApiService.saveTableSettings({
        culture: this.campaignsTableSettings.Culture,
        gridId: this.campaignsTableSettings.GridId,
        optionsJson: JSON.stringify(this.campaignsTableSettings.StateObject),
        url: this.campaignsTableSettings.Url
      }).then((result: Result) => {
        if (!result.Success) {
          HelperService.showError(result.Message);
        }
      });
    }
  }

  onColumnSelect(column: GridColumn) {
    const selectedCampaignGridColumnsIdsNew = this.columns.filter((i: GridColumn) => i.visible)
          .map((i: GridColumn) => i.id);
    this.setSelectedCampaignGridColumnsIds(selectedCampaignGridColumnsIdsNew);
    column.sortable = column.visible;

    let settingsColumn: TableSettingsStateColumn | undefined = this.campaignsTableSettings.StateObject.columns.find(this.findTableSettingsColumn(column));
    if (settingsColumn) {
      HelperService.onColumnSelectInitSettingsColumn(settingsColumn, column);
    } else {
      settingsColumn = HelperService.createTableSettingsColumn(column);
      this.campaignsTableSettings.StateObject.columns.push(settingsColumn);
    }
    settingsColumn = this.campaignsTableSettings.StateObject.dataSource.fields.find(this.findTableSettingsColumn(column));
    if (settingsColumn) {
      HelperService.onColumnSelectInitSettingsColumn(settingsColumn, column);
    } else {
      settingsColumn = HelperService.createTableSettingsColumn(column);
      this.campaignsTableSettings.StateObject.dataSource.fields.push(settingsColumn);
    }
  }

  onExportCampaignsToExcelClick() {
    this.campaignsPayload.page = 1;
    this.campaignsPayload.pageSize = ConfigService.maxIntValue;
    let loader = this.$loading.show();
    this.getCampaigns(this.campaignsPayload).then((campaigns: DataSourceResult) => {
      CampaignsApiService.exportCampaignsToExcel(campaigns, this.columns).then((fileUrl: string) => {
        window.location.href = fileUrl;
      }).catch((error) => {
        console.error(error);
      }).finally(() => {
        loader.hide();
      });
    });
  }

  onSearchChange(e: Event) {
    this.setSearchValue((e.target as HTMLInputElement).value);
  }

  //#endregion Handlers


  //#region Private methods

  private findTableSettingsColumn(column: GridColumn): (settingsColumn: TableSettingsStateColumn) => boolean {
    return (settingsColumn: TableSettingsStateColumn) => {
      let result = settingsColumn.field && settingsColumn.field === column.name
            || !settingsColumn.field && settingsColumn.title.toLowerCase() === column.label.toLowerCase();
      return result;
    }
  }

  //#endregion Private methods
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.grid-headline {
  display: flex;
  align-items: center;
  padding: 18px 21px;
  border-bottom: 1px solid #d3d6e2;
  position: relative;
  z-index: 1;
  @media (max-width: 991px) {
    padding: 14px 10px 14px 15px;
  }
  @media (max-width: 399px) {
    justify-content: space-between;
  }
  @media (max-width: 359px) {
    display: block;
    padding: 0;
  }
  .select-columns-container {
    padding-left: 20px;
    position: relative;
    @media (max-width: 991px) {
      padding-left: 15px;
    }
    @media (max-width: 399px) {
      padding-left: 5px;
    }
    @media (max-width: 359px) {
      padding: 15px 15px;
      border-bottom: 1px solid $lightd;
    }
    .button-text-w {
      margin-left: 11px;
    }
    .dropdown-menu {
      top: 46px;
      right: -22px;
      @media (max-width: 991px) {
        top: 43px;
        right: -11px;
      }
      @media (max-width: 359px) {
        top: 39px;
        right: -16px;
      }

      .dropdown-content {
        border: 1px solid #d3d6e2;
        box-shadow: 0px 0px 4px #d3d6e2;
        width: 529px;
        padding: 25px 25px 10px;
        @media (max-width: 991px) {
          border-radius: 0;
        }
        @media (max-width: 575px) {
          width: 384px;
          padding: 15px 15px 0;
        }
        @media (max-width: 399px) {
          width: 345px;
        }
        @media (max-width: 360px) {
          width: 330px;
        }

        @media (max-width: 359px) {
          width: 290px;
        }
      }
      .dropdown-item {
        columns: 3;
        @media (max-width: 575px) {
          columns: 2;
        }
        .field {
          margin-bottom: 14px;
          @media (max-width: 575px) {
            margin-bottom: 12px;
          }
        }
      }
    }
  }
  .border {
    display: block;
    height: 33px;
    width: 1px;
    background: #dcdcdc;
    @media (max-width: 991px) {
      height: 35px;
    }
    @media (max-width: 359px) {
      display: none;
    }
  }
  .search-container {
    padding: 0 25px;
    flex: 1;
    margin: 0;
    .input {
      border: none;
      box-shadow: none;
      padding: 0 9px;
      height: 33px;
      min-width: 230px;
    }
  }
  .export-button-container {
    padding-right: 20px;
    @media (max-width: 399px) {
      padding-right: 10px;
    }
    @media (max-width: 359px) {
      padding: 15px 15px;
    }
    .export-button {
      border-color: #269339;
      color: #269339;
      @media (max-width: 991px) {
        font-size: 16px;
        height: 35px;
        min-width: 120px;
      }
      &:hover {
        color: #fff;
        background: #269339;
      }
      > span {
        display: flex;
        align-items: center;
        .text {
          margin-right: 9px;
        }
      }
    }
  }
}

html[dir="ltr"] {
  .grid-headline {
    @media (max-width: 991px) {
      padding: 14px 15px 14px 10px;
    }
    .select-columns-container {
      padding: 0 20px 0 0;
      @media (max-width: 991px) {
        padding: 0 15px 0 0;
      }
      @media (max-width: 399px) {
        padding: 0 5px 0 0;
      }
      @media (max-width: 359px) {
        padding: 15px 15px;
      }
      .button-text-w {
        margin: 0 11px 0 0;
      }
    }
    .export-button-container {
      padding: 0 0 0 20px;
      @media (max-width: 399px) {
        padding: 0 0 0 10px;
      }
      @media (max-width: 359px) {
        padding: 15px 15px;
      }
    }
  }
}
</style>
